/**
 * Phase 5T — Payroll Rules admin page.
 *
 * 2 tabs:
 *   1. Categories — overview grid + click-to-edit detail form
 *   2. Statutory  — single page form with all org-wide constants
 *
 * Permissions: HR can view, super_admin can edit. UI auto-disables save
 * buttons for non-super-admin users (server-side enforced too).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  ScrollText, Plus, Pencil, RotateCcw, Save, AlertTriangle,
  Clock, Briefcase, Sparkles, Trash2, RefreshCw, Building2, Copy,
} from "lucide-react";
import { toast } from "sonner";

const SYSTEM_CODES = new Set([
  "white_collar", "white_collar_female", "blue_collar", "driver", "manual_entry",
]);

export default function PayrollRules() {
  const auth = useAuth();
  const isSuper = auth?.user?.role === "super_admin";

  return (
    <div className="space-y-4" data-testid="payroll-rules-page">
      <header className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <ScrollText className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Phase 5T · Payroll
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Payroll Rules
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Categories aur statutory rates yahan se edit karein. Change save
            karte hi agle payroll run pe apply hoga.
            {!isSuper && (
              <span className="text-amber-700 ml-1 font-semibold">
                ⓘ Read-only — sirf Super Admin edit kar sakte hain.
              </span>
            )}
          </p>
        </div>
      </header>

      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories" data-testid="pr-tab-categories">
            <Briefcase className="h-3.5 w-3.5 mr-1" /> Categories
          </TabsTrigger>
          <TabsTrigger value="units" data-testid="pr-tab-units">
            <Building2 className="h-3.5 w-3.5 mr-1" /> Units
          </TabsTrigger>
          <TabsTrigger value="statutory" data-testid="pr-tab-statutory">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> Statutory & Splits
          </TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="mt-4">
          <CategoriesTab isSuper={isSuper} />
        </TabsContent>
        <TabsContent value="units" className="mt-4">
          <UnitsTab isSuper={isSuper} />
        </TabsContent>
        <TabsContent value="statutory" className="mt-4">
          <StatutoryTab isSuper={isSuper} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===========================================================================
// CATEGORIES TAB
// ===========================================================================
function CategoriesTab({ isSuper }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFor, setEditingFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll-rules/categories");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    if (!window.confirm("Seed default categories (idempotent)?")) return;
    try {
      const { data } = await api.post("/payroll-rules/seed-defaults");
      toast.success(`Seeded ${data.categories_seeded.length} category rows`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const onDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? In-use ones will be deactivated.`)) return;
    try {
      const { data } = await api.delete(`/payroll-rules/categories/${cat.code}`);
      toast.success(data.soft_deleted
        ? `Deactivated — ${data.employees_using} employees still using`
        : "Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {rows.length} categories defined
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="pr-cat-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          {isSuper && (
            <>
              <Button variant="outline" size="sm" onClick={seed}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Seed defaults
              </Button>
              <Button size="sm" className="brand-bg brand-bg-hover text-white"
                      onClick={() => setEditingFor({ __new: true })}
                      data-testid="pr-cat-new">
                <Plus className="h-3.5 w-3.5 mr-1" /> New category
              </Button>
            </>
          )}
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      <div className="overflow-x-auto bg-white ring-1 ring-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] text-slate-600 uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-right">Shift</th>
              <th className="px-3 py-2 text-center">Time</th>
              <th className="px-3 py-2 text-center">Grade thresh</th>
              <th className="px-3 py-2 text-center">Late rules</th>
              <th className="px-3 py-2 text-center">OT</th>
              <th className="px-3 py-2 text-center">EL/PL</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-t border-slate-100"
                  data-testid={`pr-cat-row-${r.code}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                <td className="px-3 py-2 font-semibold">{r.name}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.shift_hours}h
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  <Clock className="h-3 w-3 inline mr-0.5" />
                  {r.shift_start_hhmm}→{r.shift_end_hhmm}
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  {(r.grade_p_threshold_pct || 0) > 0
                    ? `P>${r.grade_p_threshold_pct}% · H>${r.grade_h_threshold_pct}%`
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  {(r.late_grace_count_free || 0) > 0
                    ? `${r.late_grace_minutes}min, ${r.late_grace_count_free} free`
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  <Badge tone={r.ot_mode === "none" ? "slate" : "emerald"}>
                    {r.ot_mode}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  {r.el_pl_applicable ? "✓" : "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.active !== false
                    ? <Badge tone="emerald">Active</Badge>
                    : <Badge tone="slate">Inactive</Badge>}
                </td>
                <td className="px-3 py-2 text-right">
                  {isSuper && (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline"
                              onClick={() => setEditingFor(r)}
                              data-testid={`pr-cat-edit-${r.code}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      {!SYSTEM_CODES.has(r.code) && (
                        <Button size="sm" variant="outline" className="text-red-600"
                                onClick={() => onDelete(r)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-xs text-slate-400">
                No categories. Click "Seed defaults" to bootstrap.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingFor && (
        <CategoryEditDialog
          row={editingFor.__new ? null : editingFor}
          onClose={() => setEditingFor(null)}
          onSaved={() => { setEditingFor(null); load(); }}
        />
      )}
    </div>
  );
}

function CategoryEditDialog({ row, onClose, onSaved }) {
  const isNew = !row;
  const [f, setF] = useState(row || {
    code: "",
    name: "",
    shift_hours: 9,
    shift_start_hhmm: "09:30",
    shift_end_hhmm: "18:30",
    grade_p_threshold_pct: 80,
    grade_h_threshold_pct: 40,
    late_grace_minutes: 15,
    late_grace_count_free: 8,
    late_grace_deduction_days: 0.5,
    late_hard_deduction_days: 0.5,
    ot_mode: "signed",
    el_pl_applicable: true,
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async () => {
    if (!f.code.trim() || !f.name.trim()) {
      toast.warning("Code aur Name dono required");
      return;
    }
    setSaving(true);
    try {
      const url = isNew
        ? "/payroll-rules/categories"
        : `/payroll-rules/categories/${f.code}`;
      const method = isNew ? "post" : "patch";
      await api[method](url, f);
      toast.success(isNew ? "Created ✓" : "Updated ✓");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "New category" : `Edit: ${f.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Section title="Identity">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code (e.g. supervisor)" required>
                <Input value={f.code} disabled={!isNew}
                       onChange={(e) => set("code", e.target.value.toLowerCase()
                                                        .replace(/[^a-z0-9_]/g, "_"))}
                       data-testid="pr-cat-code" />
              </Field>
              <Field label="Display name" required>
                <Input value={f.name} onChange={(e) => set("name", e.target.value)}
                       data-testid="pr-cat-name" />
              </Field>
            </div>
          </Section>

          <Section title="Shift">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Hours">
                <Input type="number" step="0.5" value={f.shift_hours}
                       onChange={(e) => set("shift_hours", parseFloat(e.target.value))}
                       data-testid="pr-cat-hours" />
              </Field>
              <Field label="Start (HH:MM)">
                <Input value={f.shift_start_hhmm}
                       onChange={(e) => set("shift_start_hhmm", e.target.value)}
                       data-testid="pr-cat-start" />
              </Field>
              <Field label="End (HH:MM)">
                <Input value={f.shift_end_hhmm}
                       onChange={(e) => set("shift_end_hhmm", e.target.value)}
                       data-testid="pr-cat-end" />
              </Field>
            </div>
          </Section>

          <Section title="Grade thresholds (% of shift)">
            <div className="grid grid-cols-2 gap-3">
              <Field label="P (Present) above %"
                     hint="worked > X% of shift = P. Set 0 = any worked = P (blue-collar style)">
                <Input type="number" value={f.grade_p_threshold_pct}
                       onChange={(e) => set("grade_p_threshold_pct", parseFloat(e.target.value))}
                       data-testid="pr-cat-p-thresh" />
              </Field>
              <Field label="H (Half day) above %"
                     hint="X% ≤ worked ≤ P% = H. Below = A.">
                <Input type="number" value={f.grade_h_threshold_pct}
                       onChange={(e) => set("grade_h_threshold_pct", parseFloat(e.target.value))}
                       data-testid="pr-cat-h-thresh" />
              </Field>
            </div>
          </Section>

          <Section title="Late mark rules">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Grace minutes" hint="Minutes after shift_start = grace late zone">
                <Input type="number" value={f.late_grace_minutes}
                       onChange={(e) => set("late_grace_minutes", parseInt(e.target.value))}
                       data-testid="pr-cat-grace-min" />
              </Field>
              <Field label="Free grace count" hint="First N grace lates per month free, then ½ day each">
                <Input type="number" value={f.late_grace_count_free}
                       onChange={(e) => set("late_grace_count_free", parseInt(e.target.value))}
                       data-testid="pr-cat-grace-free" />
              </Field>
              <Field label="Grace deduction (days)">
                <Input type="number" step="0.25" value={f.late_grace_deduction_days}
                       onChange={(e) => set("late_grace_deduction_days", parseFloat(e.target.value))} />
              </Field>
              <Field label="Hard late deduction (days)">
                <Input type="number" step="0.25" value={f.late_hard_deduction_days}
                       onChange={(e) => set("late_hard_deduction_days", parseFloat(e.target.value))} />
              </Field>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              Set both = 0 to disable late tracking entirely (blue-collar / driver).
            </p>
          </Section>

          <Section title="Other">
            <div className="grid grid-cols-2 gap-3">
              <Field label="OT computation"
                     hint="signed = OT can be negative (under-shift); positive_only = floor at 0; none = no OT">
                <Select value={f.ot_mode} onValueChange={(v) => set("ot_mode", v)}>
                  <SelectTrigger data-testid="pr-cat-ot-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="signed">Signed (current default)</SelectItem>
                    <SelectItem value="positive_only">Positive only</SelectItem>
                    <SelectItem value="none">None (e.g. drivers)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="EL / PL applicable?"
                     hint="Earned + Privilege leave eligibility">
                <Switch checked={!!f.el_pl_applicable}
                        onCheckedChange={(v) => set("el_pl_applicable", v)} />
              </Field>
              <Field label="Active?">
                <Switch checked={f.active !== false}
                        onCheckedChange={(v) => set("active", v)} />
              </Field>
            </div>
          </Section>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  className="brand-bg brand-bg-hover text-white"
                  data-testid="pr-cat-save">
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// UNITS TAB — per-unit shift override + copy-to-employees
// ===========================================================================
function UnitsTab({ isSuper }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // unit_id -> {shift_start_hhmm, shift_end_hhmm, shift_hours}
  const [saving, setSaving] = useState(null);
  const [copying, setCopying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll-rules/units");
      setRows(data || []);
      // seed drafts with current values
      const d = {};
      for (const u of data || []) {
        d[u.id] = {
          shift_start_hhmm: u.shift_start_hhmm || "",
          shift_end_hhmm: u.shift_end_hhmm || "",
          shift_hours: u.shift_hours != null ? u.shift_hours : "",
        };
      }
      setDrafts(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setDraft = (unitId, k, v) =>
    setDrafts((x) => ({ ...x, [unitId]: { ...x[unitId], [k]: v } }));

  const save = async (unit) => {
    setSaving(unit.id);
    try {
      const d = drafts[unit.id] || {};
      const payload = {
        shift_start_hhmm: d.shift_start_hhmm || null,
        shift_end_hhmm: d.shift_end_hhmm || null,
        shift_hours: d.shift_hours !== "" ? parseFloat(d.shift_hours) : null,
      };
      await api.patch(`/payroll-rules/units/${unit.id}`, payload);
      toast.success(`${unit.name} shift updated ✓`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(null); }
  };

  const copyToEmployees = async (unit, overrideExisting) => {
    if (!window.confirm(
      `Copy shift_hours=${unit.shift_hours} to ${unit.employee_count} ` +
      `employees in ${unit.name}?\n\n` +
      (overrideExisting
        ? "⚠ This will OVERWRITE any existing shift_hours on those employees."
        : "Only employees without a shift_hours value will be updated.")
    )) return;
    setCopying(unit.id);
    try {
      const { data } = await api.post(
        `/payroll-rules/units/${unit.id}/copy-to-employees`
        + `?override_existing=${overrideExisting}`,
      );
      toast.success(
        `${data.modified} employees updated · shift_hours = ${data.shift_hours}`,
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Copy failed");
    } finally { setCopying(null); }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
        <b>Priority order</b> jab payroll calculate hota hai:
        <ol className="ml-5 list-decimal mt-1 space-y-0.5">
          <li><b>Unit override</b> (yeh table) — sabse high priority.</li>
          <li>Employee record ka <code>shift_hours</code> field.</li>
          <li>Category default.</li>
        </ol>
        Unit set karke "<b>Copy to employees</b>" click karein toh wahi value
        har employee pe materialise ho jaayegi (legacy code paths bhi auto pick up).
      </div>

      <div className="space-y-3">
        {rows.map((u) => {
          const d = drafts[u.id] || {};
          const dirty = (
            (d.shift_start_hhmm || "") !== (u.shift_start_hhmm || "")
            || (d.shift_end_hhmm || "") !== (u.shift_end_hhmm || "")
            || (String(d.shift_hours) || "")
                  !== (u.shift_hours != null ? String(u.shift_hours) : ""));
          return (
            <div key={u.id}
                 className="bg-white ring-1 ring-slate-200 rounded-lg p-4"
                 data-testid={`pr-unit-${u.name.replace(/\s+/g, "")}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-bravura-700" />
                  <div>
                    <div className="font-bold text-slate-900">{u.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {u.employee_count} employees ·
                      {u.shift_start_hhmm
                        ? <span className="ml-1 text-emerald-700">
                            Override: {u.shift_start_hhmm}→{u.shift_end_hhmm}, {u.shift_hours}h
                          </span>
                        : <span className="ml-1 text-slate-400">No override (using category defaults)</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Shift start (HH:MM)">
                  <Input value={d.shift_start_hhmm || ""}
                         disabled={!isSuper}
                         onChange={(e) => setDraft(u.id, "shift_start_hhmm", e.target.value)}
                         placeholder="e.g. 09:00"
                         data-testid={`pr-unit-${u.name.replace(/\s+/g, "")}-start`} />
                </Field>
                <Field label="Shift end (HH:MM)">
                  <Input value={d.shift_end_hhmm || ""}
                         disabled={!isSuper}
                         onChange={(e) => setDraft(u.id, "shift_end_hhmm", e.target.value)}
                         placeholder="e.g. 18:00"
                         data-testid={`pr-unit-${u.name.replace(/\s+/g, "")}-end`} />
                </Field>
                <Field label="Shift hours"
                       hint="Default 9. Override per unit if different (e.g. 12 for 12-hr shifts).">
                  <Input type="number" step="0.5" value={d.shift_hours ?? ""}
                         disabled={!isSuper}
                         onChange={(e) => setDraft(u.id, "shift_hours", e.target.value)}
                         placeholder="9"
                         data-testid={`pr-unit-${u.name.replace(/\s+/g, "")}-hours`} />
                </Field>
              </div>

              {isSuper && (
                <div className="flex items-center gap-2 mt-3 justify-end">
                  {dirty && (
                    <Button variant="outline" size="sm"
                            onClick={() => setDrafts((x) => ({
                              ...x,
                              [u.id]: {
                                shift_start_hhmm: u.shift_start_hhmm || "",
                                shift_end_hhmm: u.shift_end_hhmm || "",
                                shift_hours: u.shift_hours != null ? u.shift_hours : "",
                              },
                            }))}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Revert
                    </Button>
                  )}
                  <Button size="sm" disabled={!dirty || saving === u.id}
                          onClick={() => save(u)}
                          className="brand-bg brand-bg-hover text-white"
                          data-testid={`pr-unit-${u.name.replace(/\s+/g, "")}-save`}>
                    <Save className="h-3.5 w-3.5 mr-1" />
                    {saving === u.id ? "Saving…" : "Save shift"}
                  </Button>
                  {u.shift_hours != null && (
                    <>
                      <Button size="sm" variant="outline"
                              disabled={copying === u.id}
                              onClick={() => copyToEmployees(u, false)}
                              data-testid={`pr-unit-${u.name.replace(/\s+/g, "")}-copy-new`}>
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        Copy to new only
                      </Button>
                      <Button size="sm"
                              disabled={copying === u.id}
                              onClick={() => copyToEmployees(u, true)}
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`pr-unit-${u.name.replace(/\s+/g, "")}-copy-all`}>
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        {copying === u.id ? "Copying…"
                          : `Copy to all ${u.employee_count}`}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ===========================================================================
// STATUTORY TAB
// ===========================================================================
function StatutoryTab({ isSuper }) {
  const [stat, setStat] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payroll-rules/statutory");
      setStat(data);
      setOriginal(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setStat((x) => ({ ...x, [k]: v }));

  const splitsTotal = useMemo(() => {
    if (!stat) return 1;
    return (parseFloat(stat.split_basic || 0)
            + parseFloat(stat.split_hra || 0)
            + parseFloat(stat.split_conv || 0));
  }, [stat]);

  const dirty = useMemo(() => {
    if (!stat || !original) return false;
    return JSON.stringify(stat) !== JSON.stringify(original);
  }, [stat, original]);

  const save = async () => {
    if (Math.abs(splitsTotal - 1) > 0.001) {
      toast.error(`Splits must total 100%. Currently ${(splitsTotal * 100).toFixed(1)}%`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/payroll-rules/statutory", stat);
      setStat(data);
      setOriginal(data);
      toast.success("Saved ✓");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (!stat) return <div className="text-sm text-slate-500">Loading…</div>;

  const dis = !isSuper;

  return (
    <div className="space-y-4">
      <Section title="Salary Split (must total 100%)">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Basic %">
            <Input type="number" step="0.01" disabled={dis} value={stat.split_basic}
                   onChange={(e) => set("split_basic", parseFloat(e.target.value))}
                   data-testid="pr-stat-split-basic" />
          </Field>
          <Field label="HRA %">
            <Input type="number" step="0.01" disabled={dis} value={stat.split_hra}
                   onChange={(e) => set("split_hra", parseFloat(e.target.value))}
                   data-testid="pr-stat-split-hra" />
          </Field>
          <Field label="Conveyance %">
            <Input type="number" step="0.01" disabled={dis} value={stat.split_conv}
                   onChange={(e) => set("split_conv", parseFloat(e.target.value))}
                   data-testid="pr-stat-split-conv" />
          </Field>
        </div>
        <div className={`mt-2 text-xs ${Math.abs(splitsTotal - 1) > 0.001
                                          ? "text-red-700 font-semibold"
                                          : "text-emerald-700"}`}>
          Total: {(splitsTotal * 100).toFixed(2)}%{" "}
          {Math.abs(splitsTotal - 1) > 0.001 && (
            <span className="inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Must equal 100%
            </span>
          )}
        </div>
      </Section>

      <Section title="ESI">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Threshold ₹ (on Basic+DA)"
                 hint="Employee with Basic+DA ≤ this → ESI applies">
            <Input type="number" disabled={dis} value={stat.esi_threshold}
                   onChange={(e) => set("esi_threshold", parseFloat(e.target.value))}
                   data-testid="pr-stat-esi-thresh" />
          </Field>
          <Field label="Employee rate (e.g. 0.0075 = 0.75%)">
            <Input type="number" step="0.0005" disabled={dis} value={stat.esi_emp_rate}
                   onChange={(e) => set("esi_emp_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="Employer rate (e.g. 0.0325 = 3.25%)">
            <Input type="number" step="0.0005" disabled={dis} value={stat.esi_emr_rate}
                   onChange={(e) => set("esi_emr_rate", parseFloat(e.target.value))} />
          </Field>
        </div>
      </Section>

      <Section title="PF (Provident Fund)">
        <div className="grid grid-cols-3 gap-3">
          <Field label="PF wage cap ₹">
            <Input type="number" disabled={dis} value={stat.pf_wage_cap}
                   onChange={(e) => set("pf_wage_cap", parseFloat(e.target.value))} />
          </Field>
          <Field label="Employee rate (e.g. 0.12)">
            <Input type="number" step="0.001" disabled={dis} value={stat.pf_emp_rate}
                   onChange={(e) => set("pf_emp_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="EPS rate">
            <Input type="number" step="0.001" disabled={dis} value={stat.eps_rate}
                   onChange={(e) => set("eps_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="EPS cap ₹">
            <Input type="number" disabled={dis} value={stat.eps_cap_amount}
                   onChange={(e) => set("eps_cap_amount", parseFloat(e.target.value))} />
          </Field>
          <Field label="EPF rate">
            <Input type="number" step="0.001" disabled={dis} value={stat.epf_rate}
                   onChange={(e) => set("epf_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="EDLI rate">
            <Input type="number" step="0.001" disabled={dis} value={stat.edli_rate}
                   onChange={(e) => set("edli_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="PF Admin rate">
            <Input type="number" step="0.001" disabled={dis} value={stat.pf_admin_rate}
                   onChange={(e) => set("pf_admin_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="PF Admin cap ₹">
            <Input type="number" disabled={dis} value={stat.pf_admin_cap}
                   onChange={(e) => set("pf_admin_cap", parseFloat(e.target.value))} />
          </Field>
        </div>
      </Section>

      <Section title="Statutory Bonus (Payment of Bonus Act)">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Eligibility wage ₹"
                 hint="Employee eligible if (Basic+DA) ≤ this">
            <Input type="number" disabled={dis} value={stat.bonus_eligibility_wage}
                   onChange={(e) => set("bonus_eligibility_wage", parseFloat(e.target.value))}
                   data-testid="pr-stat-bonus-elig" />
          </Field>
          <Field label="Bonus rate (e.g. 0.0833 = 8.33%)">
            <Input type="number" step="0.0001" disabled={dis} value={stat.bonus_rate}
                   onChange={(e) => set("bonus_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="Wage floor ₹"
                 hint="bonus = rate × max(floor, state_min_wage)">
            <Input type="number" disabled={dis} value={stat.bonus_wage_floor}
                   onChange={(e) => set("bonus_wage_floor", parseFloat(e.target.value))} />
          </Field>
          <Field label="State minimum wage ₹"
                 hint="Rajasthan currently ₹7,438">
            <Input type="number" disabled={dis} value={stat.rajasthan_min_wage}
                   onChange={(e) => set("rajasthan_min_wage", parseFloat(e.target.value))} />
          </Field>
        </div>
      </Section>

      <Section title="Other">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Gratuity rate (e.g. 0.0481)">
            <Input type="number" step="0.0001" disabled={dis} value={stat.gratuity_rate}
                   onChange={(e) => set("gratuity_rate", parseFloat(e.target.value))} />
          </Field>
          <Field label="Professional Tax ₹"
                 hint="Rajasthan = 0">
            <Input type="number" disabled={dis} value={stat.pt_rate}
                   onChange={(e) => set("pt_rate", parseFloat(e.target.value))} />
          </Field>
        </div>
      </Section>

      <div className="sticky bottom-2 bg-white border border-slate-300 rounded-lg p-3 shadow-md flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {dirty
            ? <span className="text-amber-700">Unsaved changes</span>
            : <span>All saved · last edit {original.updated_at?.slice(0, 16) || "—"}</span>}
        </div>
        <div className="flex gap-2">
          {dirty && (
            <Button variant="outline" size="sm"
                    onClick={() => setStat(original)}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Revert
            </Button>
          )}
          {isSuper && (
            <Button onClick={save} disabled={!dirty || saving}
                    className="brand-bg brand-bg-hover text-white"
                    data-testid="pr-stat-save">
              <Save className="h-3.5 w-3.5 mr-1" />
              {saving ? "Saving…" : "Save statutory rules"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================
function Section({ title, children }) {
  return (
    <div className="bg-white ring-1 ring-slate-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-medium text-slate-700 block mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
}

function Badge({ tone = "slate", children }) {
  const cls = {
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-700",
    slate: "bg-slate-100 text-slate-600",
  }[tone];
  return (
    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded ${cls}`}>
      {children}
    </span>
  );
}
